import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Animated,
  BackHandler,
  Easing,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useKeyboardHeight } from '../../hooks/useKeyboardHeight';
import {
  colors,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * A panel that rises from the bottom edge over whatever is already on screen.
 *
 * Used instead of pushing a new screen wherever the flow should not lose its
 * place - opening a service from Home, or signing in part-way through adding
 * one. The screen behind stays mounted and visible through the scrim, so
 * closing returns you exactly where you were.
 *
 * It is a `Modal` rather than an absolutely-positioned view so it escapes the
 * parent's stacking and clipping - Home's tab bar carries an elevation, and a
 * plain sibling view would slide underneath it.
 *
 * `animationType` is left at "none": the slide and the scrim fade are driven
 * here so they run together and so the exit can finish before the modal is
 * torn down. Modal's own slide would fight both.
 */

const OPEN_MS = 260;
const CLOSE_MS = 190;

type Props = {
  visible: boolean;
  onClose: () => void;
  /**
   * Height as a fraction of the screen. Omit to let the sheet size to its
   * content, which is what a short form wants.
   */
  heightRatio?: number;
  /** Pinned below the scrolling content, outside the safe-area inset. */
  footer?: ReactNode;
  children: ReactNode;
  /**
   * Called once the sheet is really gone - not when it is asked to close.
   *
   * ---------------------------------------------------------------------------
   * FOR OPENING THE NEXT SHEET, AND NOTHING ELSE
   * ---------------------------------------------------------------------------
   * Every sheet is a native `Modal`, and iOS cannot present one while another
   * is still on screen. Closing one sheet and opening the next in the same
   * tick - "Add new" in the address sheet opening sign-in, say - worked on
   * Android, where dialogs may overlap. On iOS the second sheet never appeared,
   * and the window it had tried to use stayed over the app swallowing every
   * touch until the app was killed.
   *
   * So a caller that wants to open something after this sheet closes does it
   * from here. On iOS this is the Modal's own `onDismiss`, which is the one
   * signal that the native window has actually left; elsewhere it is the end of
   * the slide-out.
   */
  onClosed?: () => void;
};

/**
 * How long iOS is given to report the dismissal before `onClosed` fires
 * anyway. `onDismiss` is the right signal and normally arrives within a frame
 * or two of the slide finishing; this only exists so a missed one can never
 * leave "Add new" doing nothing.
 */
const IOS_DISMISS_FALLBACK_MS = 400;

function BottomSheet({
  visible,
  onClose,
  heightRatio,
  footer,
  children,
  onClosed,
}: Props) {
  const { height } = useWindowDimensions();
  const resp = useResponsive();
  const insets = useSafeAreaInsets();

  /*
   * Kept mounted for the length of the close animation. Driving the Modal
   * straight from `visible` would unmount the sheet on the first frame of the
   * exit, so it would vanish rather than slide away.
   */
  const [mounted, setMounted] = useState(visible);

  /*
   * `onClosed` is fired at most once per open, and only for a close that
   * followed a real open - never for a sheet that mounts already closed, and
   * never for a dismissal that arrives after the sheet was reopened.
   */
  const wasOpen = useRef(false);
  const visibleRef = useRef(visible);
  const onClosedRef = useRef(onClosed);
  const dismissFallback = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    visibleRef.current = visible;
    onClosedRef.current = onClosed;
  });

  const fireClosed = useCallback(() => {
    if (dismissFallback.current) {
      clearTimeout(dismissFallback.current);
      dismissFallback.current = null;
    }
    if (!wasOpen.current || visibleRef.current) {
      return;
    }
    wasOpen.current = false;
    onClosedRef.current?.();
  }, []);

  useEffect(
    () => () => {
      if (dismissFallback.current) {
        clearTimeout(dismissFallback.current);
      }
    },
    [],
  );

  const slide = useRef(new Animated.Value(0)).current; // 0 closed, 1 open

  useEffect(() => {
    if (visible) {
      wasOpen.current = true;
      if (dismissFallback.current) {
        clearTimeout(dismissFallback.current);
        dismissFallback.current = null;
      }
      setMounted(true);
      const open = Animated.timing(slide, {
        toValue: 1,
        duration: OPEN_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      });
      open.start();
      return () => open.stop();
    }

    const close = Animated.timing(slide, {
      toValue: 0,
      duration: CLOSE_MS,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    });
    close.start(({ finished }) => {
      if (!finished) {
        return;
      }
      setMounted(false);
      if (Platform.OS === 'ios') {
        /* `onDismiss` below is the real signal; this is only the net under
           it - see `IOS_DISMISS_FALLBACK_MS`. */
        dismissFallback.current = setTimeout(
          fireClosed,
          IOS_DISMISS_FALLBACK_MS,
        );
      } else {
        fireClosed();
      }
    });
    return () => close.stop();
  }, [fireClosed, slide, visible]);

  /*
   * How far the keyboard has come up, and therefore how far this sheet must.
   *
   * Read as a height rather than a boolean because the sheet now lifts itself
   * by that exact amount - see the render. `KeyboardAvoidingView` used to do
   * the lifting, and stopped working on Android 15+ where the window no longer
   * resizes; `useKeyboardHeight` explains that in full.
   */
  const keyboardHeight = useKeyboardHeight();
  const keyboardUp = keyboardHeight > 0;

  /**
   * How tall this sheet's own window lays out, and how tall it was before the
   * keyboard arrived.
   *
   * ---------------------------------------------------------------------------
   * THE SHEET ENDED UP A KEYBOARD ABOVE THE KEYBOARD, ON SOME PHONES ONLY
   * ---------------------------------------------------------------------------
   * The lift below assumes the window did NOT make room, which is true on iOS
   * and on Android 15+. `useKeyboardHeight` is supposed to catch the older
   * Android case where it did, by watching `useWindowDimensions` shrink and
   * subtracting whatever the window had already given up.
   *
   * That check does not work in here. A `Modal` is its own window, and
   * `useWindowDimensions` inside one does not report the resize - so on a
   * handset that DOES still honour `adjustResize`, the hook saw no shrink,
   * returned the full keyboard height, and the sheet was carried up by it a
   * second time. The reported symptom was exactly that: the sign-in sheet's
   * logo and heading pushed off the top of the screen, with a keyboard's worth
   * of empty page between the sheet and the keys.
   *
   * So the room already made is MEASURED rather than inferred. `onLayout` on
   * the root reports what this window actually laid out, and the height it had
   * while the keyboard was down is the yardstick - the same subtraction the
   * hook does, against the one thing that is reliable in here.
   *
   * Both are refs-in-state: the value has to survive a re-render AND cause one,
   * because the padding below is computed from it.
   */
  const [rootHeight, setRootHeight] = useState(0);
  const restingHeight = useRef(0);
  if (!keyboardUp && rootHeight > 0) {
    restingHeight.current = rootHeight;
  }

  /*
   * What the window has already surrendered to the keyboard, and therefore how
   * much of it the sheet still has to climb.
   *
   * Zero on a window that did not resize, which is the common case and leaves
   * the behaviour exactly as it was. A full keyboard height on one that did,
   * which is the bug.
   *
   * `restingHeight` of 0 means the first layout has not happened yet - lifting
   * by the full height is the right guess there, since that is the majority
   * platform behaviour and the layout that corrects it is one frame away.
   */
  const roomAlreadyMade =
    restingHeight.current > 0 && rootHeight > 0
      ? Math.max(0, restingHeight.current - rootHeight)
      : 0;
  const lift = Math.max(0, keyboardHeight - roomAlreadyMade);

  // Android's back gesture should dismiss the sheet, not the screen behind it.
  useEffect(() => {
    if (!visible) {
      return;
    }
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      onClose();
      return true;
    });
    return () => sub.remove();
  }, [onClose, visible]);

  /*
   * A ratio of the screen, then held to something a person can actually reach.
   *
   * The callers' ratios were chosen against an 844pt phone. In landscape - and
   * on a short phone - the same 0.75 is a sheet a couple of hundred points
   * tall with a scrolling form crammed into it, so it gets a floor. On a
   * tablet the same ratio is a 800pt sheet whose top edge is nowhere near the
   * thumb that has to close it, so it gets a ceiling too.
   */
  /*
   * What is actually left to put a sheet in.
   *
   * The keyboard's height comes off the top of the window, and the status bar
   * comes off what remains - the root is `flex-end`, so anything taller than
   * this does not shrink, it overflows UPWARD and disappears past the notch.
   * That is exactly what happened with a full-height sheet and the keyboard
   * open: 760pt of sheet in 508pt of space put its top 252pt off-screen, so
   * only the submit button was visible above the status bar.
   *
   * A small floor so the arithmetic can never produce a zero- or
   * negative-height sheet on a very short window.
   */
  /*
   * `lift`, not `keyboardHeight` - see the note on it. On a window that
   * resized itself the keys are already outside this box, so taking their
   * height off again would cap the sheet at half the room it actually has.
   */
  const available = Math.max(240, (rootHeight || height) - lift - insets.top);

  /*
   * A ratio of the screen, then held to something a person can actually reach
   * - and finally to what the keyboard has left.
   */
  const sheetHeight = heightRatio
    ? Math.round(
        Math.min(
          Math.max(height * heightRatio, Math.min(height * 0.9, 420)),
          Math.min(height * 0.92, 720),
          available,
        ),
      )
    : undefined;
  const bottomInset = keyboardUp
    ? spacing.md
    : Math.max(insets.bottom, spacing.md);

  return (
    <Modal
      transparent
      /*
       * Driven from `mounted` rather than rendered conditionally. Unmounting a
       * Modal never fires `onDismiss`, and on iOS that callback is the only
       * reliable moment another sheet may be presented - see `onClosed`.
       * A Modal that is not visible renders nothing, so a closed sheet costs
       * nothing either way.
       */
      visible={mounted}
      animationType="none"
      onRequestClose={onClose}
      onDismiss={Platform.OS === 'ios' ? fireClosed : undefined}
    >
      {/*
        The sheet has to ride above the keyboard.

        A `Modal` is its own window, so Android's `adjustResize` never reaches
        it - and from Android 15 that setting does nothing anywhere, because
        the window stays full height and the keyboard arrives as an inset. The
        sheet keeps the whole screen and the keys cover whatever sits at its
        bottom, which is exactly where a form's input and submit button are.

        So the root is padded by however much of the keyboard this window has
        NOT already made room for - see `lift`. `flex-end` then parks the sheet
        on top of the keys rather than behind them, on every platform, whether
        or not the window resized.
      */}
      <View
        style={[styles.root, { paddingBottom: lift }]}
        onLayout={event => setRootHeight(event.nativeEvent.layout.height)}
      >
        <Animated.View style={[styles.scrim, { opacity: slide }]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={onClose}
          />
        </Animated.View>

        <Animated.View
          style={[
            styles.sheet,
            /*
              `available`, not a share of the whole window - a content-sized
              sheet is capped by what the keyboard has left just as a
              ratio-sized one is. This is the branch the sign-in sheet uses.
            */
            sheetHeight
              ? { height: sheetHeight }
              : { maxHeight: Math.min(height * 0.9, available) },
            /*
             * Full-bleed on a phone, a centred panel on a tablet.
             *
             * Every sheet in this app is a form or a short list - the service
             * detail, the slot picker, sign-in. Run edge to edge on a 10-inch
             * screen those become a label at the far left and its control at
             * the far right, with the eye unable to connect the two. The width
             * cap is the same column the pages use, so a sheet and the page
             * behind it line up.
             */
            resp.isTablet ? [styles.centred, { width: resp.sheetWidth }] : null,
            /*
             * With a footer, that strip owns the inset. Without one the sheet's
             * own content is what meets the bottom of the screen, so it has to
             * clear the gesture bar or nav buttons itself - otherwise the last
             * control sits underneath them.
             */
            footer ? null : { paddingBottom: bottomInset },
            {
              transform: [
                {
                  translateY: slide.interpolate({
                    inputRange: [0, 1],
                    // Its own height would need measuring; the screen height is
                    // always at least that, so it is guaranteed off-screen.
                    outputRange: [height, 0],
                  }),
                },
              ],
            },
          ]}
        >
          <View style={styles.grabber} />

          {/*
            `flex: 1` only when the sheet has a definite height.

            `flex: 1` also sets `flexBasis: 0`. In a content-sized sheet (no
            `heightRatio`) the parent's height comes from its children, so a
            zero-basis child that is told to fill it resolves to zero - the
            scrim appears and the sheet does not. `flexShrink` alone lets the
            content set the height while still yielding to `maxHeight`.
          */}
          <View style={sheetHeight ? styles.bodyFill : styles.bodyAuto}>
            {children}
          </View>

          {footer ? (
            <View
              style={[styles.footer, { paddingBottom: bottomInset }]}
            >
              {footer}
            </View>
          ) : null}
        </Animated.View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  /* Spelled out rather than spread from `StyleSheet.absoluteFillObject`, which
     this React Native version does not expose on the type. */
  scrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.overlay,
  },
  sheet: {
    backgroundColor: colors.background,
    borderTopLeftRadius: radii.xl,
    borderTopRightRadius: radii.xl,
    overflow: 'hidden',
    ...shadows.md,
  },
  /* Tablets only - see the render. */
  centred: {
    alignSelf: 'center',
  },
  grabber: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    marginTop: spacing.sm,
    backgroundColor: colors.borderStrong,
  },
  bodyFill: {
    flex: 1,
  },
  bodyAuto: {
    flexShrink: 1,
  },
  footer: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
});

export default BottomSheet;
